import { MedplumClient } from '@medplum/core';
import { Patient, Task } from '@medplum/fhirtypes';

/**
 * Summary information about an intake task
 */
export interface IntakeTaskSummary {
  id: string;
  status: 'requested' | 'in-progress' | 'completed' | 'failed';
  totalForms: number;
  completedForms: number;
  createdDate: string;
  dueDate?: string;
  requestedBy?: string;
  title: string;
}

export class TaskService {
  /**
   * Gets intake tasks for a patient
   */
  static async getIntakeTasks(medplum: MedplumClient, patient: Patient): Promise<IntakeTaskSummary[]> {
    if (!patient.id) return [];
    
    try {
      // Search for intake workflow tasks for this patient
      const searchResult = await medplum.search('Task', {
        focus: `Patient/${patient.id}`,
        'code': 'https://progressnotes.app/fhir/CodeSystem/task-type|intake-workflow',
        _sort: '-_lastUpdated'
      });
      
      if (!searchResult.entry || searchResult.entry.length === 0) {
        return [];
      }
      
      const taskSummaries: IntakeTaskSummary[] = [];
      
      for (const entry of searchResult.entry) {
        const task = entry.resource as Task;
        
        // Get total forms from task output
        const formsAssigned = task.output?.find(o => o.type?.text === 'forms-assigned')?.valueInteger || 0;
        const formsTotal = task.output?.find(o => o.type?.text === 'forms-total')?.valueInteger || 0;
        
        // Get forms actually completed by searching QuestionnaireResponses
        const formResponses = await medplum.search('QuestionnaireResponse', {
          subject: `Patient/${patient.id}`,
          'extension': 'https://progressnotes.app/fhir/StructureDefinition/form-assignment|true'
        });
        
        const completedForms = formResponses.entry?.filter(
          e => e.resource?.status === 'completed'
        ).length || 0;
        
        // Get requester name if available
        let requesterName = '';
        if (task.requester?.reference) {
          try {
            const practitioner = await medplum.readReference(task.requester);
            // Check if the resource has a name property (excludes Device type)
            if ('name' in practitioner && Array.isArray(practitioner.name) && practitioner.name.length > 0) {
              const name = practitioner.name[0];
              if (typeof name === 'object') {
                requesterName = name.text || 
                              `${Array.isArray(name.given) ? name.given[0] || '' : ''} ${name.family || ''}`.trim();
              }
            }
          } catch (err) {
            console.error('Error fetching requester details:', err);
          }
        }
        
        taskSummaries.push({
          id: task.id || '',
          status: task.status as 'requested' | 'in-progress' | 'completed' | 'failed',
          totalForms: formsTotal,
          completedForms,
          createdDate: task.authoredOn || '',
          dueDate: task.executionPeriod?.end,
          requestedBy: requesterName,
          title: task.description || 'Intake Forms'
        });
      }
      
      return taskSummaries;
    } catch (err) {
      console.error('Error fetching intake tasks:', err);
      return [];
    }
  }
}